import type { ImageEffect } from '@components/img/img.view';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import classNames from 'classnames';
import type { UseImageResult } from 'hooks/use-image';
import useImageProps from 'hooks/use-image';
import { useMemo } from 'react';
import { validateString } from 'utils/validate-string';

import style from './img.module.scss';

interface Props {
  contain?: boolean | undefined;
  fill: boolean;
  imageSource: SanityImageSource;
  imageStyle?: 'arch' | undefined;
  className?: string | undefined;
  effect?: ImageEffect | undefined;
}

interface State {
  imageClass: string;
  wrapperClass: string;
  imageProps: UseImageResult | null;
}

const imageClass = validateString(style.image);
const wrapperClass = validateString(style.wrapper);
const fillClass = validateString(style.fill);
const containClass = validateString(style.contain);
const archClass = validateString(style.arch);
const colorShadowClass = validateString(style.colorShadow);
const outlineEffectClass = validateString(style.outlineEffect);
const outlineRightEffectClass = validateString(style.outlineRightEffect);
const boxRightEffectClass = validateString(style.boxRightEffect);

export default function useImage({
  contain = false,
  className: classNameProp,
  fill,
  imageSource,
  imageStyle,
  effect,
}: Props): State {
  return {
    imageClass: useMemo(
      () =>
        classNames(classNameProp, imageClass, {
          [fillClass]: fill,
          [containClass]: contain,
        }),
      [classNameProp, contain, fill],
    ),
    wrapperClass: useMemo(
      () =>
        classNames(wrapperClass, {
          [archClass]: imageStyle === 'arch',
          [colorShadowClass]: effect === 'color',
          [outlineEffectClass]: effect === 'outline',
          [outlineRightEffectClass]: effect === 'outline-right',
          [boxRightEffectClass]: effect === 'box-right',
        }),
      [imageStyle, effect],
    ),
    imageProps: useImageProps(imageSource),
  };
}
